import React, { useState, useEffect } from "react";
import {
  Box, Container, Button, Alert, Snackbar, useTheme, TextField, Grid
} from "@mui/material";

import { useCloneDocuments } from "../../hooks/useClonations/useCloneDocuments.jsx";
import { useFlexibleClone } from "../../hooks/useClonations/useFlexibleClone.jsx";
import { useMassiveClone } from "../../hooks/useClonations/useMassiveClone.jsx";
import { useSections } from "../../hooks/useSections/useGetSections.jsx";
import { useGetNotesBySection } from "../../hooks/useClonations/useGetNotesBySection.jsx";
import { toastAlert } from "../../utils/AlertContext";

import CoincidenceTab from "./panelClonations/CoincidenceTab.jsx";
import FlexibleTab from "./panelClonations/FlexibleTab.jsx";
import MultipleTab from "./panelClonations/MultipleTab.jsx";
import SuccessModal from "./panelClonations/SuccessModal.jsx";
import { useCloneMultipleSections } from "../../hooks/useClonations/useCloneMultipleSections.jsx";

const TABS = ["COINCIDENCIA", "FLEXIBLE", "MULTIPLE"];

const INITIAL_SUCCESS = {
  mode: "",
  notes: [],
  sectionOrigin: "",
  sectionTarget: "",
  document: "",
  sections: [],
};

export const ClonationsPanel = ({ token, idDocument, nameDocument, date }) => {
  const theme = useTheme();

  const [activeTab, setActiveTab] = useState("COINCIDENCIA");

  const [targetId, setTargetId] = useState("");
  const [targetName, setTargetName] = useState("");
  const [targetSectionId, setTargetSectionId] = useState("");

  const [isNoteMode, setIsNoteMode] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [selectedNotes, setSelectedNotes] = useState([]);
  const [selectedSectionOrigin, setSelectedSectionOrigin] = useState(null);
  const [selectedSources, setSelectedSources] = useState({});

  const [resetKey, setResetKey] = useState(0);
  const [notification, setNotification] = useState({ open: false, type: "info", message: "" });
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [successDetails, setSuccessDetails] = useState(INITIAL_SUCCESS);

  const { executeFullClonation, loading: loadingClone } = useCloneDocuments();
  const { executeFlexibleClone, loadingFlex } = useFlexibleClone();
  const { executeMassiveClone, loadingMassive } = useMassiveClone();
  const { executeMultipleClone, loadingMultiple } = useCloneMultipleSections();
  const { sections: originSections } = useSections(token, idDocument);
  const { sections: destinationSections, loading: loadingDestSections } = useSections(token, targetId);
  const [selectedDate, setSelectedDate] = useState(date);
  const { notes, loadingNotes } = useGetNotesBySection(token, expandedSection, selectedDate, idDocument);

  useEffect(() => {
    setTargetSectionId("");
  }, [targetId]);

  useEffect(() => {
    setSelectedNotes([]);
    setSelectedSectionOrigin(null);
    setSelectedSources({});
    setExpandedSection(null);
    setTargetSectionId("");
  }, [isNoteMode, activeTab]);

  const handleToggleNote = (nota) => {
    const idSintesis = nota.id_sintesis || nota.ID_SINTESIS;
    if (!idSintesis) return;
    const identifier = Number(idSintesis);
    setSelectedNotes((prev) =>
      prev.includes(identifier) ? prev.filter((i) => i !== identifier) : [...prev, identifier]
    );
  };

  const handleExecuteCoincidence = async () => {
    const result = await executeFullClonation(token, idDocument, targetId, selectedDate);
    if (result.success) {
      setSuccessDetails({
        mode: "COINCIDENCIA",
        document: targetName,
        sections: result.sections || [],
        details: result.details || []
      });
      setOpenSuccessModal(true);
    } else {
      toastAlert(result.error || "Error al clonar el documento", "error");
    }
  };

  const handleActionFlexible = async () => {
    const originSec = isNoteMode ? expandedSection : selectedSectionOrigin;

    if (!targetId || !originSec || !targetSectionId) {
      setNotification({ open: true, type: "warning", message: "Faltan datos de destino." });
      return;
    }

    const targetSecName =
      destinationSections?.find((s) => s.ID_SECCION === targetSectionId)?.NOMBRE_SECCION ||
      "Sección Destino";
    const originSecName =
      originSections?.find((s) => s.ID_SECCION === originSec)?.NOMBRE_SECCION ||
      "Sección Origen";

    let result;

    if (isNoteMode) {
      const params = {
        originDocumentId: Number(idDocument),
        originSectionId: Number(originSec),
        targetDocumentId: Number(targetId),
        targetSectionId: Number(targetSectionId),
        items: selectedNotes,
        date: selectedDate,
      };
      result = await executeFlexibleClone(token, params);
      if (result.success) {
        setSuccessDetails({
          mode: "NOTAS",
          notes: selectedNotes,
          sectionTarget: targetSecName,
          document: targetName,
          details: result.data.details,
        });
        setOpenSuccessModal(true);
      }
    } else {
      const params = {
        originDocumentId: Number(idDocument),
        originSectionId: Number(originSec),
        targetDocumentId: Number(targetId),
        targetSectionId: Number(targetSectionId),
        date: selectedDate,
      };
      result = await executeMassiveClone(token, params);
      if (result.success) {
        setSuccessDetails({
          mode: "MASIVO",
          sectionOrigin: originSecName,
          sectionTarget: targetSecName,
          document: targetName,
          details: result.data.details,
        });
        toastAlert(result.message || "Clonación masiva realizada con éxito", "success");
        setOpenSuccessModal(true);
      }
    }

    if (!result.success) {
      setNotification({ open: true, type: "error", message: result.error || "Error en el proceso" });
    }
  };

  const handleActionMultiple = async () => {
    if (!targetId || !targetSectionId || Object.keys(selectedSources).length === 0) {
      setNotification({ open: true, type: "warning", message: "Faltan datos para realizar la clonación." });
      return;
    }

    const targetSecName =
      destinationSections?.find((s) => s.ID_SECCION === targetSectionId)?.NOMBRE_SECCION ||
      "Sección Destino";

    const sources = Object.entries(selectedSources).map(([secIdStr, config]) => {
      const originSectionId = Number(secIdStr);
      if (config.cloneAll) {
        return { originSectionId };
      }
      return { originSectionId, noteIds: config.notes };
    });

    const params = {
      originDocumentId: Number(idDocument),
      targetDocumentId: Number(targetId),
      targetSectionId: Number(targetSectionId),
      sources,
      date: selectedDate,
    };

    const result = await executeMultipleClone(token, params);
    
    if (result.success) {
      if (result.partial) {
        toastAlert("Clonación completada con errores parciales", "warning");
      } else {
        toastAlert(result.data.message || "Clonación múltiple realizada con éxito", "success");
      }
      setSuccessDetails({
        mode: "MULTIPLE",
        document: targetName,
        sectionTarget: targetSecName,
        results: result.data.summary, 
        details: result.data.results,
      });
      setOpenSuccessModal(true);
    } else {
      setNotification({ open: true, type: "error", message: result.error || "Error en el proceso de clonación múltiple" });
    }
  };

  const handleResetForNewClone = () => {
    setTargetId("");
    setTargetName("");
    setSelectedNotes([]);
    setSelectedSectionOrigin(null);
    setSelectedSources({});
    setOpenSuccessModal(false);
    setResetKey((prev) => prev + 1);
  };

  return (
    <Box sx={{
      width: "100%",
      height: "100%",
      overflowY: "auto",
      bgcolor: "background.default",
      p: { xs: 2, md: 5 },
    }}>
      <Container maxWidth="lg">

        <Box sx={{ display: "flex", gap: 1.5, mb: 4 }}>
          {TABS.map((tab) => (
            <Button
              key={tab}
              onClick={() => setActiveTab(tab)}
              sx={{
                px: 3, borderRadius: "10px", textTransform: "none", fontWeight: 600,
                bgcolor: activeTab === tab ? "primary.main" : "transparent",
                color: activeTab === tab ? "#fff" : "text.secondary",
                border: "1px solid",
                borderColor: activeTab === tab ? "primary.main" : theme.palette.divider,
                "&:hover": { bgcolor: activeTab === tab ? "primary.dark" : "action.hover" },
              }}
            >
              {tab}
            </Button>
          ))}
          <Grid item xs={12} container spacing={2}>
            <Grid item xs={12} sm={3} md={3}>
              <TextField
                label="Fecha"
                type="date"
                variant="outlined"
                value={selectedDate}
                onChange={(event) => setSelectedDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    borderRadius: "12px",
                    fontSize: "14px",
                    padding: "8px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "8px",
                  }
                }}
                inputProps={{
                  max: new Date().toISOString().split("T")[0],
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {activeTab === "COINCIDENCIA" ? (
          <CoincidenceTab
            key={resetKey}
            token={token}
            idDocument={idDocument}
            nameDocument={nameDocument}
            targetId={targetId}
            setTargetId={setTargetId}
            targetName={targetName}
            setTargetName={setTargetName}
            loadingClone={loadingClone}
            onExecute={handleExecuteCoincidence}
            date={selectedDate}
          />
        ) : activeTab === "FLEXIBLE" ? (
          <FlexibleTab
            key={resetKey}
            token={token}
            idDocument={idDocument}
            originSections={originSections}
            isNoteMode={isNoteMode}
            setIsNoteMode={setIsNoteMode}
            expandedSection={expandedSection}
            setExpandedSection={setExpandedSection}
            notes={notes}
            loadingNotes={loadingNotes}
            selectedNotes={selectedNotes}
            onToggleNote={handleToggleNote}
            selectedSectionOrigin={selectedSectionOrigin}
            setSelectedSectionOrigin={setSelectedSectionOrigin}
            targetId={targetId}
            setTargetId={setTargetId}
            targetName={targetName}
            setTargetName={setTargetName}
            targetSectionId={targetSectionId}
            setTargetSectionId={setTargetSectionId}
            destinationSections={destinationSections}
            loadingDestSections={loadingDestSections}
            onConfirm={handleActionFlexible}
            loadingFlex={loadingFlex}
            loadingMassive={loadingMassive}
            date={selectedDate}
          />
        ) : (
          <MultipleTab
            key={resetKey}
            token={token}
            idDocument={idDocument}
            originSections={originSections}
            expandedSection={expandedSection}
            setExpandedSection={setExpandedSection}
            notes={notes}
            loadingNotes={loadingNotes}
            targetId={targetId}
            setTargetId={setTargetId}
            targetName={targetName}
            setTargetName={setTargetName}
            targetSectionId={targetSectionId}
            setTargetSectionId={setTargetSectionId}
            destinationSections={destinationSections}
            loadingDestSections={loadingDestSections}
            selectedSources={selectedSources}
            setSelectedSources={setSelectedSources}
            onConfirm={handleActionMultiple}
            loadingMultiple={loadingMultiple}
            date={selectedDate}
          />
        )}
      </Container>

      <SuccessModal
        open={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
        onReset={handleResetForNewClone}
        successDetails={successDetails}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert severity={notification.type} variant="filled" sx={{ width: "100%" }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClonationsPanel;